import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, User, Calendar } from 'lucide-react';
import ScrollAnimation from './ScrollAnimation';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock form submission
    alert('הודעה נשלחה בהצלחה! נחזור אליך תוך 24 שעות.');
    setFormData({ name: '', email: '', phone: '', message: '', serviceType: '' });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: <Phone className="w-7 h-7 text-amber-600" />,
      title: "טלפון",
      details: ["052-894-3335"],
      description: "זמינים לשיחות"
    },
    {
      icon: <Mail className="w-7 h-7 text-amber-600" />,
      title: "אימייל", 
      details: ["shimonn1968@gmail.com"],
      description: "נענה תוך 24 שעות"
    },
    {
      icon: <MapPin className="w-7 h-7 text-amber-600" />,
      title: "כתובת",
      details: ["הסולל 6, חולון"],
      description: "אזור חניה נוח"
    },
    {
      icon: <Clock className="w-7 h-7 text-amber-600" />,
      title: "שעות פעילות",
      details: ["ראשון - חמישי: 7:00-18:00", "שישי: 7:00-15:00", "שבת: סגור"],
      description: "זמינים גם לתיאומים מחוץ לשעות"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <ScrollAnimation animation="fadeInUp">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-amber-900 mb-8">
              צור קשר
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-10 rounded-full"></div>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-light">
              מעוניינים לשדרג את הרהיטים שלכם? נשמח לעזור ולספק ייעוץ מקצועי ללא התחייבות
              <br />
              <span className="text-amber-700 font-medium">קבלו הצעת מחיר תוך 24 שעות</span>
            </p>
          </div>
        </ScrollAnimation>

        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => (
            <ScrollAnimation key={index} animation="fadeInUp" delay={index * 100}>
              <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-14 h-14 bg-amber-100 rounded-full mr-4 ml-2">
                    {info.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-amber-900">{info.title}</h3>
                    <p className="text-sm text-amber-600">{info.description}</p>
                  </div>
                </div>
                <div className="space-y-1 flex-grow">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-700 font-medium">{detail}</p>
                  ))}
                </div>
              </div>
            </ScrollAnimation>
          ))}
        </div>
        
        {/* Contact Methods */}
        <ScrollAnimation animation="fadeInUp" delay={400}>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-2xl shadow-2xl">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-amber-900 mb-4">צרו איתנו קשר</h3>
                <p className="text-gray-600">בחרו את הדרך הנוחה לכם ליצירת קשר</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <a 
                  href="tel:052-894-3335"
                  className="flex items-center justify-center bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  <Phone className="w-6 h-6 mr-3 ml-2" />
                  <div className="text-right">
                    <div className="text-lg">התקשרו עכשיו</div>
                    <div className="text-amber-200">052-894-3335</div>
                  </div>
                </a>
                
                <a 
                  href="https://wa.me/972528943335?text=שלום, מעוניין לקבל הצעת מחיר"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  <svg className="w-6 h-6 mr-3 ml-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <div className="text-right">
                    <div className="text-lg">WhatsApp</div>
                    <div className="text-green-200">הודעה מהירה</div>
                  </div>
                </a>
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">או שלחו לנו אימייל</p>
                <a 
                  href="mailto:shimonn1968@gmail.com"
                  className="inline-flex items-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  shimonn1968@gmail.com
                </a>
              </div>
            </div>
          </div>
        </ScrollAnimation>

      </div>
    </section>
  );
};

export default Contact;